import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import {
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassAccentButton,
  GlassEmpty,
} from '../components/glass/GlassUi';

// Wheel segments: BBA red leads, then bright distinct hues; dark labels on the lighter hues stay readable
const WHEEL_SEGMENTS = [
  { fill: '#F80707', text: '#FFFFFF' },
  { fill: '#FFB547', text: '#1C1916' },
  { fill: '#5BC0EB', text: '#1C1916' },
  { fill: '#7FD9B0', text: '#1C1916' },
  { fill: '#B9A3F5', text: '#1C1916' },
  { fill: '#F28BA8', text: '#1C1916' },
  { fill: '#93B4F5', text: '#1C1916' },
  { fill: '#FF7A68', text: '#1C1916' },
  { fill: '#6FCFCF', text: '#1C1916' },
  { fill: '#E8C88A', text: '#1C1916' },
];

const ConferenceSpinwheel = () => {
  const { conferenceId } = useParams();

  const [conference, setConference] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const fetchConferenceData = async () => {
      try {
        // Fetch conference data
        const conferenceRef = doc(db, 'conferences', conferenceId);
        const conferenceSnap = await getDoc(conferenceRef);

        if (!conferenceSnap.exists()) {
          console.error('Conference not found');
          setLoading(false);
          return;
        }

        const conferenceData = {
          id: conferenceSnap.id,
          ...conferenceSnap.data(),
          startDate: conferenceSnap.data().startDate?.toDate() || null,
          endDate: conferenceSnap.data().endDate?.toDate() || null,
          date: conferenceSnap.data().date?.toDate() || conferenceSnap.data().startDate?.toDate() || null
        };
        setConference(conferenceData);

        // Fetch leads from subcollection
        const leadsRef = collection(db, 'conferences', conferenceId, 'leads');
        const leadsQuery = query(leadsRef, orderBy('date', 'desc'));
        const leadsSnapshot = await getDocs(leadsQuery);

        const leadsData = leadsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || doc.data().createdAt?.toDate() || null
        }));

        setLeads(leadsData);
      } catch (error) {
        console.error('Error fetching conference data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (conferenceId) {
      fetchConferenceData();
    }
  }, [conferenceId]);

  const spinWheel = () => {
    if (leads.length === 0 || isSpinning) return;

    setIsSpinning(true);
    setSelectedLead(null);

    // Random rotation (multiple full spins + random angle)
    const baseRotations = 5; // Number of full rotations
    const randomAngle = Math.random() * 360;
    const totalRotation = baseRotations * 360 + randomAngle;
    const newRotation = rotation + totalRotation;

    setRotation(newRotation);

    // Calculate which lead is selected based on final rotation
    setTimeout(() => {
      const normalizedAngle = (360 - (newRotation % 360)) % 360;
      const segmentAngle = 360 / leads.length;
      const selectedIndex = Math.floor(normalizedAngle / segmentAngle);
      const finalIndex = selectedIndex >= leads.length ? 0 : selectedIndex;

      setSelectedLead(leads[finalIndex]);
      setIsSpinning(false);
    }, 5000); // Match animation duration
  };

  const backTo = `/crm/conferences-reports/${conferenceId}`;

  if (loading) {
    return (
      <GlassPage>
        <GlassPageHeader backTo={backTo} backTitle="Back to conference" title={null} />
        <div className="mt-8 text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="mt-4 text-gray-600 dark:text-white/60">Loading conference...</p>
        </div>
      </GlassPage>
    );
  }

  if (!conference) {
    return (
      <GlassPage>
        <GlassPageHeader backTo={backTo} backTitle="Back to conference" title={null} />
        <GlassCard className="p-6">
          <GlassEmpty className="h-32">Conference not found</GlassEmpty>
        </GlassCard>
      </GlassPage>
    );
  }

  const title = <>{conference.name}<br />Spin Wheel</>;

  if (leads.length === 0) {
    return (
      <GlassPage>
        <GlassPageHeader backTo={backTo} backTitle="Back to conference" title={title} />
        <GlassCard className="mx-auto max-w-xl p-6">
          <GlassEmpty className="h-40 text-lg">No leads available to spin</GlassEmpty>
        </GlassCard>
      </GlassPage>
    );
  }

  const segmentAngle = 360 / leads.length;
  // Keep the last segment from matching the first one it sits next to
  const segmentAt = (index) => {
    let c = index % WHEEL_SEGMENTS.length;
    if (leads.length > 1 && index === leads.length - 1 && c === 0) c = 1;
    return WHEEL_SEGMENTS[c];
  };

  return (
    <GlassPage>
      <GlassPageHeader backTo={backTo} backTitle="Back to conference" title={title} />

      <div className="flex flex-col items-center justify-center">
        <GlassCard className="flex w-full max-w-2xl flex-col items-center px-6 py-10" delay={0}>
          {/* Wheel Container */}
          <div className="relative mb-10">
            {/* Soft glass halo around the wheel */}
            <div aria-hidden="true" className="absolute -inset-4 rounded-full border border-black/[0.06] bg-white/40 shadow-[0_20px_60px_-24px_rgba(28,25,22,0.35)] dark:border-white/[0.08] dark:bg-white/[0.04] dark:shadow-[0_24px_70px_-28px_rgba(0,0,0,0.85)]" />
            <div className="relative w-96 h-96">
              {/* Pointer */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 z-10 drop-shadow-[0_4px_6px_rgba(0,0,0,0.35)]">
                <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-[#F80707]"></div>
              </div>

              {/* Wheel */}
              <svg
                width="384"
                height="384"
                viewBox="0 0 384 384"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
                }}
              >
                <defs>
                  {leads.map((lead, index) => (
                    <linearGradient key={index} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={segmentAt(index).fill} stopOpacity="1" />
                      <stop offset="100%" stopColor={segmentAt(index).fill} stopOpacity="0.85" />
                    </linearGradient>
                  ))}
                </defs>
                {leads.map((lead, index) => {
                  const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                  const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
                  const largeArcFlag = segmentAngle > 180 ? 1 : 0;

                  const x1 = 192 + 192 * Math.cos(startAngle);
                  const y1 = 192 + 192 * Math.sin(startAngle);
                  const x2 = 192 + 192 * Math.cos(endAngle);
                  const y2 = 192 + 192 * Math.sin(endAngle);

                  const midAngle = (startAngle + endAngle) / 2;
                  const textX = 192 + 140 * Math.cos(midAngle);
                  const textY = 192 + 140 * Math.sin(midAngle);

                  return (
                    <g key={index}>
                      <path
                        d={`M 192 192 L ${x1} ${y1} A 192 192 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                        fill={`url(#gradient-${index})`}
                        stroke="#FBFAF8"
                        strokeWidth="2"
                      />
                      <text
                        x={textX}
                        y={textY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={segmentAt(index).text}
                        fontSize="14"
                        fontWeight="bold"
                        className="pointer-events-none"
                        transform={`rotate(${index * segmentAngle + segmentAngle / 2 - 90}, ${textX}, ${textY})`}
                      >
                        {lead.firstName || lead.first_name || 'Lead'} {lead.lastName || lead.last_name || index + 1}
                      </text>
                    </g>
                  );
                })}
              </svg>

              {/* Hub (static, sits over the wheel's centre) */}
              <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-4 border-[#FBFAF8] bg-[#1C1916] shadow-lg">
                <span className="h-3 w-3 rounded-full bg-[#F80707]" />
              </div>
            </div>
          </div>

          {/* Spin Button */}
          <GlassAccentButton
            onClick={spinWheel}
            disabled={isSpinning || leads.length === 0}
            className="px-8 py-4 text-lg font-bold"
          >
            {isSpinning ? 'Spinning...' : 'Spin the Wheel!'}
          </GlassAccentButton>
        </GlassCard>

        {/* Selected Lead Display */}
        {selectedLead && (
          <GlassCard className="mt-8 w-full max-w-md p-8 text-center" delay={0}>
            <h2 className="mb-6 text-2xl font-medium text-gray-900 dark:text-white">
              🎉 Winner! 🎉
            </h2>
            <p className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
              {selectedLead.firstName || selectedLead.first_name || ''} {selectedLead.lastName || selectedLead.last_name || ''}
            </p>
            <p className="mt-2 text-lg text-gray-600 dark:text-white/60">
              {selectedLead.companyName || selectedLead.company || ''}
            </p>
          </GlassCard>
        )}
      </div>
    </GlassPage>
  );
};

export default ConferenceSpinwheel;

